import Button from '@/components/Button/index';
import Input from '@/components/Input/index';
import toastType from '@/constants/toast-type/index';
import ETR_API from "@/services/api/etr";
import { validateEmail } from "@/utils/validate-email";
import { isAddress } from "ethers/lib/utils";
import { uniqueId } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import useDid from '@/hooks/use-did/index';

const TableRowInput = ({
	data,
	isEdit = false,
	setIsEdit = null,
	resetCallback,
	checkDuplicate,
	fetchEtr,
	addToastMessage,
	columnWidth,
	...TableRow
}) => {
	const id = uniqueId()
	const [isAdd, setIsAdd] = useState(false)
	const [isPin, setIsPin] = useState(false)
	const [prevIsAdd, setPrevIsAdd] = useState(false);
	const { toFindOrg } = useDid();

	useEffect(() => {
		if (data) {
			setIsPin(data.isPinned);
		}
	}, [data]);

	const { register, formState: { errors, isValid }, getValues, reset, watch } = useForm({
		mode: 'onChange'
	})

	useEffect(() => {
		if (typeof resetCallback === 'function') {
			resetCallback(reset);
		}
	}, [reset, resetCallback]);

	const referenceRef = useRef(null)

	useEffect(() => {
		document.addEventListener('mousedown', handleDocumentClick)
		return () => {
			document.removeEventListener('mousedown', handleDocumentClick)
		}
	}, [])

	function isElementClicked(element, eventTarget) {
		return element.contains(eventTarget);
	}

	function findClickedElement(elements, eventTarget) {
		for (const element of elements) {
			if (isElementClicked(element, eventTarget)) {
				return element;
			}
		}
		return null;
	}

	function handleDocumentClick(event) {
		if (!referenceRef.current) {
			return;
		}

		if (isElementClicked(referenceRef.current, event.target)) {
			return;
		}

		const currentForm = document.getElementById(`add-address-${id}`);
		const addressElements = Array.from(document.querySelectorAll('[id^="add-address-"]'));
		const iconMenus = Array.from(document.querySelectorAll('[id^="icon-menu"]'));

		const clickedAddressElement = findClickedElement(addressElements, event.target);
		const clickedIconMenu = findClickedElement(iconMenus, event.target);

		const clickedOutside = !clickedAddressElement;
		const clickedAnotherForm = clickedAddressElement && clickedAddressElement.id !== `add-address-${id}`;
		const clickedAnotherIconMenu = clickedIconMenu && !isElementClicked(referenceRef.current, event.target);

		if (
			typeof setIsEdit === 'function' &&
			((clickedAnotherForm) || clickedAnotherIconMenu || (!clickedOutside && !isElementClicked(currentForm, event.target)))
		) {
			setIsEdit(false);
		} else if (clickedAnotherForm && !isEdit) {
			reset();
		}
	}

	const watchedName = watch('name');
	const watchedEmail = watch('email');
	const watchedWalletAddress = watch('wallet');

	useEffect(() => {
		if (!watchedName && !watchedEmail && !watchedWalletAddress) {
			if (prevIsAdd) {
				reset();
			}
			setIsAdd(false);
			setPrevIsAdd(false);
			return;
		}

		if (watchedName || watchedEmail || watchedWalletAddress) {
			setIsAdd(true);
			setPrevIsAdd(true);
		}
	}, [watchedName, watchedEmail, watchedWalletAddress, prevIsAdd, reset]);

	function formSubmit(event) {
		event.preventDefault();
		formHandler().catch(error => {
			console.error('Error in form submit:', error);
		});
	}

	async function formHandler() {
		if (isEdit) {
			const payload = {
				isPitstopAddress: false,
				pin: isPin,
				orgName: getValues().name,
				walletAddress: getValues().wallet,
				email: getValues().email.toLowerCase()
			}

			const [error] = await ETR_API.updateSavedAddress(payload, data.id);

			if (error) {
				return addToastMessage(error, toastType.error)
			}

			addToastMessage('Address updated successfully', toastType.success)
		}

		if (!isEdit) {
			const payload = {
				orgName: getValues().name,
				walletAddress: getValues().wallet,
				email: getValues().email.toLowerCase(),
				pin: isPin
			}

			const [error] = await ETR_API.savedAddress(payload);

			if (error) {
				return addToastMessage(error, toastType.error)
			}

			addToastMessage('Address added successfully', toastType.success)
		}

		fetchEtr()
		reset()
	}

	function handleEdit() {
		reset()
		if (isEdit) {
			setIsEdit(false)
		}
	}

	return (
		<form
			className="address-book__table__row"
			id={`add-address-${id}`}
			ref={referenceRef} {...TableRow}
			data-testid="table-row-input"
			onSubmit={formSubmit}
		>
			<div className="address-book__table__col" style={{ ...columnWidth?.find(s => s.column === 'name')?.style }}>
				<Input
					addClass="address-book__table__input"
					placeholder="Organisation Name"
					name="name"
					defaultValue={data?.name || null}
					register={register({
						required: "Field cannot be left empty",
						validate: (v) => {
							if (!data?.name || v.toLowerCase() !== data.name.toLowerCase()) {
								const duplicate = checkDuplicate(v, "name");
								if (duplicate) {
									return "This organisation already exists";
								}
							}
							return true;
						}
					})}
					error={errors.name}
					errorMessage={errors.name?.message}
				/>
			</div>
			<div className="address-book__table__col" style={{ ...columnWidth?.find(s => s.column === 'walletAddress')?.style }}>
				<Input
					addClass="address-book__table__input"
					placeholder="Wallet Address"
					name="wallet"
					defaultValue={data?.walletAddress || null}
					register={register({
						required: "Field cannot be left empty",
						validate: (v) => {
							if (!data?.walletAddress || v.toLowerCase() !== data.walletAddress.toLowerCase()) {
								const duplicate = checkDuplicate(v, "walletAddress");
								if (duplicate) {
									return "This wallet address already exists";
								}
								const validAddress = isAddress(v);
								if (!validAddress) {
									return "Invalid address format";
								}

								const isPitstopAddress = toFindOrg(v);
								if (isPitstopAddress) {
									return "You can only add a Pitstop wallet address via the Admin Portal. Please approach your administrator."
								}
							}
							return true;
						},
					})}
					error={errors.wallet}
					errorMessage={errors.wallet?.message}
				/>
			</div>
			<div className="address-book__table__col" style={{ ...columnWidth?.find(s => s.column === 'email')?.style }}>
				<Input
					addClass="address-book__table__input"
					placeholder="Email"
					name="email"
					defaultValue={data?.email || null}
					register={register({
						required: "Field cannot be left empty",
						validate: (v) => {
							if (!data?.email || v.toLowerCase() !== data.email.toLowerCase()) {
								const validEmail = validateEmail(v);

								if (!validEmail) {
									return "Invalid email format";
								}
							}
							return true;
						},
					})}
					error={errors.email}
					errorMessage={errors.email?.message}
				/>
			</div>
			<div className="address-book__table__col address-book__table__col--menu" style={{ ...columnWidth?.find(s => s.column === 'action')?.style }}>
				{(isAdd || isEdit) && (
					<div className="address-book__menu-horizontal">
						<Button
							noborder
							addClass="address-book__menu-horizontal__item"
							submit={() => setIsPin(!isPin)}
							type="button"
							text={!isPin ? "Pin" : "Unpin"}
							maxWidth="50"
							data-testid="pin-button"
						/>
						<Button
							noborder
							addClass="address-book__menu-horizontal__item"
							submit={(e) => {
								e.preventDefault()
								handleEdit()
							}}
							text="Cancel"
							maxWidth="50"
							data-testid="cancel-button"
						/>
						<Button
							round
							addClass="address-book__menu-horizontal__item"
							submit={() => formHandler()}
							type="submit"
							disabled={!isValid}
							text="Save"
							maxWidth="50"
							form="add-address"
							data-testid="save-button"
						/>
					</div>
				)}
			</div>
		</form>
	)
}

export default TableRowInput
